import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, Result, Button, Alert, Spin, Typography, Space, Steps, Descriptions } from 'antd';
import { 
  ExclamationCircleOutlined, 
  HomeOutlined, 
  ArrowLeftOutlined,
  CreditCardOutlined,
  PhoneOutlined
} from '@ant-design/icons';
import { supabase } from '../../../api/supabase';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

const LunchPaymentCancel = () => {
  const { uniqueLink } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [reservation, setReservation] = useState(null);
  const [error, setError] = useState(null);

  // Company theme colors
  const theme = {
    primary: '#000000',
    secondary: '#ffffff',
    accent: '#666666',
    lightGrey: '#f5f5f5',
    mediumGrey: '#d9d9d9',
    success: '#52c41a',
    warning: '#faad14',
    error: '#ff4d4f',
    gradient: 'linear-gradient(135deg, #000000 0%, #434343 100%)'
  };

  useEffect(() => {
    console.log('❌ Payment cancel page loaded');
    console.log('URL params:', { uniqueLink });
    console.log('Search params:', Object.fromEntries(searchParams.entries()));
    
    if (uniqueLink) {
      fetchReservationData();
    }
  }, [uniqueLink]);

  const fetchReservationData = async () => {
    setLoading(true);
    try {
      const { data, error: fetchError } = await supabase
        .from('lunch_reservations')
        .select(`
          *,
          lunch_reservation_items (*),
          lunch_signed_contracts (*)
        `)
        .eq('unique_link', uniqueLink)
        .single();

      if (fetchError) {
        throw new Error('Réservation non trouvée');
      }

      setReservation(data);
      console.log('📋 Reservation data loaded:', data);

      // Enregistrer l'annulation
      await recordCancellation(data);

    } catch (error) {
      console.error('❌ Error fetching reservation:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const recordCancellation = async (reservationData) => {
    try {
      // Enregistrer l'événement d'annulation de paiement
      const { error } = await supabase
        .from('lunch_payments')
        .insert([{
          lunch_reservation_id: reservationData.id,
          amount: reservationData.total_amount,
          payment_method: 'systempay_link',
          transaction_id: `cancelled_${Date.now()}`,
          status: 'cancelled',
          created_at: new Date().toISOString(),
          up2pay_response: JSON.stringify({
            status: 'cancelled',
            reason: 'User cancelled payment',
            cancelled_at: new Date().toISOString(),
            return_url: window.location.href
          })
        }]);

      if (error) {
        console.warn('⚠️ Warning recording cancellation:', error);
      } else {
        console.log('📝 Payment cancellation recorded');
      }

    } catch (error) {
      console.error('❌ Error recording cancellation:', error);
    }
  };

  const returnToReservation = () => {
    window.location.href = `/lunch-reservation/${uniqueLink}`;
  };

  const retryPayment = () => {
    window.open('https://jepaieenligne.systempay.fr/MASTERSKI_PILOU', '_blank');
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: theme.gradient
      }}>
        <Card style={{ textAlign: 'center', maxWidth: 400, border: `2px solid ${theme.mediumGrey}` }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Chargement de votre réservation...</Text>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: theme.gradient
      }}>
        <Card style={{ maxWidth: 500, border: `2px solid ${theme.error}` }}>
          <Result
            status="error"
            title="Erreur"
            subTitle={error}
            extra={
              <Button 
                type="primary" 
                icon={<HomeOutlined />}
                onClick={() => window.location.href = '/'}
                style={{ 
                  backgroundColor: theme.primary, 
                  borderColor: theme.primary 
                }}
              >
                Retour à l'accueil
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      background: theme.gradient,
      padding: '24px'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <Card style={{ 
          marginBottom: '24px', 
          textAlign: 'center',
          border: `2px solid ${theme.mediumGrey}`,
          background: theme.secondary
        }}>
          <Title level={2} style={{ margin: '0 0 8px 0', color: theme.primary }}>
            🏝️ Master Ski Pilou
          </Title>
          <Title level={3} style={{ margin: '0 0 16px 0', color: theme.accent }}>
            Paiement Annulé
          </Title>
        </Card>

        {/* Statut actuel */}
        <Card style={{ 
          marginBottom: '24px',
          border: `1px solid ${theme.mediumGrey}`,
          background: theme.secondary
        }}>
          <Title level={4} style={{ color: theme.primary, marginBottom: '16px' }}>
            Où en êtes-vous ?
          </Title>
          <Steps current={1} status="error" size="small">
            <Step 
              title="Sélection" 
              description="Menu sélectionné"
              status="finish"
            />
            <Step 
              title="Signature" 
              description={reservation?.lunch_signed_contracts?.length > 0 ? "Contrat signé" : "En attente"}
              status={reservation?.lunch_signed_contracts?.length > 0 ? "finish" : "wait"}
            />
            <Step 
              title="Paiement" 
              description="Paiement annulé"
              status="error"
            />
            <Step 
              title="Confirmation" 
              description="En attente"
              status="wait"
            />
          </Steps>
        </Card>

        {/* Résultat principal */}
        <Card style={{ 
          border: `2px solid ${theme.warning}`,
          background: theme.secondary
        }}>
          <Result
            status="warning"
            title={<span style={{ color: theme.warning }}>Paiement Annulé</span>}
            subTitle={
              <div>
                <Paragraph style={{ fontSize: '16px', color: theme.accent }}>
                  Bonjour <strong style={{ color: theme.primary }}>{reservation?.client_name}</strong>,<br/>
                  Vous avez annulé le processus de paiement.
                </Paragraph>
                
                <Alert
                  message="⚠️ Votre réservation est en attente"
                  description={
                    <div style={{ textAlign: 'left' }}>
                      <p><strong>Statut actuel :</strong></p>
                      <ul>
                        <li>✅ Menu sélectionné ({reservation?.lunch_reservation_items?.length || 0} articles)</li>
                        <li>{reservation?.lunch_signed_contracts?.length > 0 ? '✅' : '⏳'} Contrat {reservation?.lunch_signed_contracts?.length > 0 ? 'signé électroniquement' : 'en attente de signature'}</li>
                        <li>❌ Paiement annulé - montant: <strong>{reservation?.total_amount}€</strong></li>
                        <li>⏳ Confirmation en attente</li>
                      </ul>
                      <p><strong>Pour finaliser votre réservation, vous devez effectuer le paiement.</strong></p>
                    </div>
                  }
                  type="warning"
                  showIcon
                  style={{ margin: '24px 0', textAlign: 'left' }}
                />

                {reservation && (
                  <Descriptions 
                    column={1} 
                    bordered 
                    size="small"
                    style={{ marginTop: '24px', textAlign: 'left' }}
                    title="Détails de votre réservation"
                  >
                    <Descriptions.Item label="Date du charter">
                      {dayjs(reservation.charter_date).format('DD/MM/YYYY')}
                    </Descriptions.Item>
                    <Descriptions.Item label="Bateau">
                      {reservation.boat_name}
                    </Descriptions.Item>
                    <Descriptions.Item label="Type de sortie">
                      {reservation.trip_type}
                    </Descriptions.Item>
                    <Descriptions.Item label="Montant à payer">
                      <span style={{ color: theme.error, fontWeight: 'bold', fontSize: '16px' }}>
                        {reservation.total_amount}€
                      </span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Statut signature">
                      {reservation.lunch_signed_contracts?.length > 0 ? (
                        <span style={{ color: theme.success }}>
                          ✅ Signé le {dayjs(reservation.lunch_signed_contracts[0].signature_date).format('DD/MM/YYYY HH:mm')}
                        </span>
                      ) : (
                        <span style={{ color: theme.warning }}>⏳ En attente de signature</span>
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                )}
              </div>
            }
            extra={[
              <Space key="actions" direction="vertical" style={{ width: '100%' }}>
                <Space size="large" style={{ justifyContent: 'center', width: '100%' }}>
                  <Button 
                    type="primary" 
                    size="large"
                    icon={<CreditCardOutlined />}
                    onClick={retryPayment}
                    style={{ 
                      backgroundColor: theme.success, 
                      borderColor: theme.success,
                      minWidth: '200px'
                    }}
                  >
                    💳 Effectuer le paiement
                  </Button>
                  
                  <Button 
                    size="large"
                    icon={<ArrowLeftOutlined />}
                    onClick={returnToReservation}
                    style={{ minWidth: '200px' }}
                  >
                    Retour à ma réservation
                  </Button>
                </Space>
                
                <Button 
                  icon={<HomeOutlined />}
                  onClick={() => window.location.href = '/'}
                  style={{ width: '100%' }}
                >
                  Retour à l'accueil
                </Button>
                
                <Text type="secondary" style={{ fontSize: '12px', textAlign: 'center', display: 'block' }}>
                  💡 Astuce: Vous pouvez effectuer le paiement à tout moment avant votre sortie
                </Text>
              </Space>
            ]}
          />
        </Card>

        {/* Articles sélectionnés */}
        {reservation?.lunch_reservation_items?.length > 0 && (
          <Card style={{ 
            marginTop: '24px',
            border: `1px solid ${theme.lightGrey}`,
            background: theme.secondary
          }}>
            <Title level={4} style={{ color: theme.primary }}>🛒 Rappel de votre sélection</Title>
            <div style={{ marginBottom: '16px' }}>
              {reservation.lunch_reservation_items.map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: index < reservation.lunch_reservation_items.length - 1 ? `1px solid ${theme.lightGrey}` : 'none'
                }}>
                  <div>
                    <Text strong style={{ color: theme.primary }}>{item.item_name}</Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {item.quantity} x {item.unit_price}€
                      </Text>
                    </div>
                  </div>
                  <Text strong style={{ color: theme.primary }}>
                    {item.total_price}€
                  </Text>
                </div>
              ))}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 0 8px 0',
                borderTop: `2px solid ${theme.mediumGrey}`,
                marginTop: '16px'
              }}>
                <Text strong style={{ fontSize: '16px', color: theme.primary }}>TOTAL:</Text>
                <Text strong style={{ fontSize: '18px', color: theme.error }}>
                  {reservation.total_amount}€
                </Text>
              </div>
            </div>
          </Card>
        )}

        {/* Informations de contact et aide */}
        <Card style={{ 
          marginTop: '24px',
          textAlign: 'center',
          border: `1px solid ${theme.lightGrey}`,
          background: theme.secondary
        }}>
          <Title level={4} style={{ color: theme.primary }}>Besoin d'aide ?</Title>
          <Paragraph style={{ color: theme.accent }}>
            Si vous rencontrez des difficultés avec le paiement ou si vous avez des questions,<br/>
            n'hésitez pas à contacter Master Ski Pilou :
          </Paragraph>
          <Space direction="vertical">
            <Space>
              <PhoneOutlined style={{ color: theme.primary }} />
              <Text><strong>Téléphone :</strong> 05 90 27 91 79</Text>
            </Space>
            <Text><strong>Email :</strong> masterskipilou@example.com</Text>
            <Text><strong>Adresse :</strong> Rue Jeanne d'Arc, GUSTAVIA - 97133 Saint-Barthélemy</Text>
          </Space>
          
          <Alert
            message="💡 Conseil"
            description="Si vous avez des difficultés avec le paiement en ligne, vous pouvez aussi nous appeler directement pour finaliser votre réservation par téléphone."
            type="info"
            showIcon
            style={{ marginTop: '16px' }}
          />
        </Card>

        {/* Rappel des délais */}
        <Card style={{ 
          marginTop: '24px',
          border: `1px solid ${theme.warning}`,
          background: '#fffbe6'
        }}>
          <Alert
            message={`⏰ Date limite: ${dayjs(reservation?.charter_date).subtract(48, 'hour').format('DD/MM/YYYY HH:mm')}`}
            description={`Votre réservation expire 48h avant la sortie (${dayjs(reservation?.charter_date).format('DD/MM/YYYY')}). Pensez à finaliser votre paiement avant cette échéance.`}
            type="warning"
            showIcon
          />
        </Card>
      </div>
    </div>
  );
};

export default LunchPaymentCancel;